/**
 * Typed distinction between billing-backed memberships and manual grants.
 *
 * Some Executive memberships are granted permanently by Retail News Asia and
 * have no Stripe customer behind them. Their subscription rows carry sentinel
 * identifiers purely to satisfy NOT NULL columns. Those sentinels must never
 * reach Stripe (it answers `No such customer: …`) and must never be rendered.
 */

export type BillingSource = "stripe" | "manual";

/** Stripe customer ids are always `cus_` + an opaque alphanumeric suffix. */
const STRIPE_CUSTOMER_ID = /^cus_[A-Za-z0-9]{6,}$/;

export function isStripeCustomerId(value: unknown): value is string {
  return typeof value === "string" && STRIPE_CUSTOMER_ID.test(value);
}

/**
 * A membership is billing-backed only when a syntactically valid Stripe
 * customer id exists. Anything else — null, empty, or a sentinel such as
 * `manual_grant_no_billing_account` — is a manual grant.
 */
export function resolveBillingSource(customerId: unknown): BillingSource {
  return isStripeCustomerId(customerId) ? "stripe" : "manual";
}

/** Copy shown to members whose access is granted rather than billed. */
export const MANUAL_GRANT_DETAIL =
  "Permanent Executive access \u2014 managed by Retail News Asia. No billing action is required.";

/** The only portal failure message a member ever sees. */
export const PORTAL_GENERIC_ERROR =
  "We could not open the billing portal. Please try again in a moment.";

/** Rejection used when a manual grant asks for a portal session. */
export const PORTAL_NOT_APPLICABLE =
  "This membership is managed by Retail News Asia and has no self-service billing.";
